import React, { useState, useEffect, useMemo } from 'react';
import NovaAvatarImg from './NovaAvatarImg';
import './NovaConnectAside.css';
import { linkInline } from '../designSystem';
import { normalizeDemoChapterKey, getNovaSidebarCopy } from '../data/demoChapterNova';
import {
  LG_BTQ_DEMO_UPDATE,
  loadLgbtqPath,
  novaSidebarInclusive,
  articleTitleForPath,
} from '../data/fertilityLgbtqDemo';

const BG_SECONDARY = 'var(--color-background-secondary, #f8f8f8)';
const BORDER_SECONDARY = 'var(--color-border-secondary, #e0e0e0)';
const BORDER_TERTIARY = 'var(--color-border-tertiary, #e5e5e5)';
const TEXT_TERTIARY = 'var(--color-text-tertiary, #888)';

const EMMA_IMG =
  'https://images.squarespace-cdn.com/content/v1/648f945aaaf1c8276ed8386c/38c6ff3d-0c6f-4137-93dd-2a378f204473/edit-2.jpg';

const CONNECT_ASK_CHIPS = [
  'Who else is doing retrieval this week?',
  "What should I ask at tomorrow's scan?",
  'Find women with low AMH near me',
];

const COMMUNITY_ASK_CHIPS = [
  "Who's doing retrieval this week?",
  'Which group is best for low AMH?',
  'Find women near me in IVF',
];

function hideImgShowFallback(e) {
  e.currentTarget.style.display = 'none';
  const n = e.currentTarget.nextElementSibling;
  if (n) n.style.display = 'flex';
}

function DoctorPhoto36({ src, alt, initials, fallbackBg, fallbackColor, border }) {
  return (
    <>
      <img
        src={src}
        alt={alt}
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          objectFit: 'cover',
          objectPosition: 'center top',
          flexShrink: 0,
          border,
        }}
        onError={hideImgShowFallback}
      />
      <div
        style={{
          display: 'none',
          width: 36,
          height: 36,
          borderRadius: '50%',
          background: fallbackBg,
          color: fallbackColor,
          fontSize: 11,
          fontWeight: 700,
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          border,
        }}
      >
        {initials}
      </div>
    </>
  );
}

const CONNECT_GRADIENT_BAR_BG = 'linear-gradient(140deg, rgb(251, 234, 240), rgb(225, 245, 238))';

function NovaSidebarGradientBar({ chatExpanded, onToggleChat, chapterKey, pillText, section, connectMode }) {
  const avatarChapter = chapterKey === 'ttc' ? 'ttc' : 'fertility';
  const isFertilityDoctors = chapterKey === 'fertility' && section === 'doctors';
  const barBackground = isFertilityDoctors
    ? 'linear-gradient(135deg, #fbeaf0 0%, #fffcf9 100%)'
    : CONNECT_GRADIENT_BAR_BG;
  return (
    <div
      style={{
        background: barBackground,
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        flexShrink: 0,
        borderTop: '0.5px solid rgba(212, 83, 126, 0.15)',
        borderBottom: '0.5px solid rgba(212, 83, 126, 0.12)',
      }}
    >
      {connectMode ? (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'rgb(251, 234, 240)',
            color: 'rgb(153, 53, 86)',
            fontSize: 13,
            fontWeight: 900,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            border: 'none',
            fontFamily: "'Playfair Display', Georgia, serif",
          }}
        >
          N
        </div>
      ) : (
        <NovaAvatarImg size={32} alt="" chapter={avatarChapter} />
      )}
      <div style={{ flex: '1 1 0%', minWidth: 0, display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'rgb(153, 53, 86)' }}>Nova</span>
        <span
          style={{
            fontSize: 11,
            color: 'rgb(153, 53, 86)',
            fontWeight: connectMode ? 400 : undefined,
            opacity: connectMode ? 1 : 0.7,
          }}
        >
          — always here
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 20,
            background: 'rgba(212, 83, 126, 0.13)',
            color: 'rgb(153, 53, 86)',
            whiteSpace: 'nowrap',
          }}
        >
          {pillText}
        </span>
      </div>
      <div
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: 'rgb(29, 158, 117)',
          marginLeft: 'auto',
          flexShrink: 0,
          boxShadow: 'rgba(29, 158, 117, 0.3) 0px 0px 0px 2px',
        }}
      />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleChat();
        }}
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.7)',
          border: '0.5px solid rgba(212, 83, 126, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: 13,
          color: 'rgb(153, 53, 86)',
          marginLeft: 6,
          flexShrink: 0,
          fontFamily: 'inherit',
        }}
        aria-label={chatExpanded ? 'Collapse Nova chat' : 'Expand Nova chat'}
        aria-expanded={chatExpanded}
      >
        {chatExpanded ? '∨' : '∧'}
      </button>
    </div>
  );
}

export default function NovaFeedSidebar({
  navigate,
  setNovaDraft,
  chapter,
  section,
  novaOpen: novaOpenProp,
  onNovaOpenChange,
}) {
  const [uncontrolledNovaOpen, setUncontrolledNovaOpen] = useState(true);
  const isNovaOpenControlled = novaOpenProp !== undefined;
  const novaOpen = isNovaOpenControlled ? novaOpenProp : uncontrolledNovaOpen;
  const setNovaOpen = (next) => {
    if (isNovaOpenControlled) onNovaOpenChange?.(next);
    else setUncontrolledNovaOpen(next);
  };

  const toggleChat = () => {
    setNovaOpen(!novaOpen);
  };

  const [inputValue, setInputValue] = useState('');
  const [inputFocused, setInputFocused] = useState(false);
  const chapterKey = normalizeDemoChapterKey(chapter);
  const c = getNovaSidebarCopy(chapterKey);
  const demoBase = chapter ? `/demo/${chapter}` : null;
  const isTtcCycle = chapterKey === 'ttc' && section === 'cycle';
  const isLgbtqFertility = chapterKey === 'fertility' && section === 'lgbtq';
  const isFertilityDoctors = chapterKey === 'fertility' && section === 'doctors';
  const [lgbtqFeedKey, setLgbtqFeedKey] = useState(0);
  useEffect(() => {
    if (!isLgbtqFertility) return undefined;
    const fn = () => setLgbtqFeedKey((k) => k + 1);
    window.addEventListener(LG_BTQ_DEMO_UPDATE, fn);
    return () => window.removeEventListener(LG_BTQ_DEMO_UPDATE, fn);
  }, [isLgbtqFertility]);
  const lgbtqPath = useMemo(() => (isLgbtqFertility ? loadLgbtqPath() : null), [isLgbtqFertility, lgbtqFeedKey]);
  const inc = isLgbtqFertility ? novaSidebarInclusive(lgbtqPath) : null;
  const articleTitle = isLgbtqFertility ? articleTitleForPath(lgbtqPath) : '';
  const isConnectRail = section === 'connect' && chapterKey === 'fertility';
  const isCommunityFertilityRail = section === 'community' && chapterKey === 'fertility';

  const handleChipClick = (text) => {
    if (setNovaDraft) setNovaDraft(text);
    if (navigate) navigate('/nova');
  };

  const handleSend = () => {
    if (inputValue && setNovaDraft) setNovaDraft(inputValue);
    if (navigate) navigate('/nova');
  };

  const sectionLabel = {
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: TEXT_TERTIARY,
    marginBottom: 8,
  };

  const contentCard = {
    background: BG_SECONDARY,
    border: `0.5px solid ${BORDER_TERTIARY}`,
    borderRadius: 10,
    padding: '12px 16px',
  };

  const linkBtn = { ...linkInline, fontSize: 12, marginTop: 5, textAlign: 'left', paddingRight: 4, display: 'block' };

  if (isCommunityFertilityRail) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 0%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <div
          className="com-community-rail"
          style={{
            flex: '1 1 0%',
            minHeight: 0,
            overflowY: 'auto',
            background: 'var(--white)',
          }}
        >
          <div className="com-rail-page-title">Your community</div>

          <div className="com-rail-label">Friends online now</div>
          <div className="com-rail-row">
            <div className="com-rail-av-wrap">
              <div className="com-rail-av">JM</div>
              <span className="com-rail-av-dot" aria-hidden />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="com-rail-name">Jasmine M.</div>
              <div className="com-rail-sub">Cycle 2 · stim day 8 like you</div>
            </div>
            <button type="button" className="com-rail-msg" onClick={() => navigate?.('/signup')}>
              Message
            </button>
          </div>
          <div className="com-rail-row">
            <div className="com-rail-av-wrap">
              <div className="com-rail-av">AK</div>
              <span className="com-rail-av-dot" aria-hidden />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="com-rail-name">Ava K.</div>
              <div className="com-rail-sub">Transfer prep · Cincinnati</div>
            </div>
            <button type="button" className="com-rail-msg" onClick={() => navigate?.('/signup')}>
              Message
            </button>
          </div>

          <div className="com-rail-label">Groups for you</div>
          <div className="com-rail-group-row">
            <div className="com-rail-group-icon" aria-hidden />
            <div style={{ minWidth: 0 }}>
              <div className="com-rail-group-name">IVF Warriors — Cycle 1</div>
              <div className="com-rail-group-sub">3,240 members · your cycle</div>
            </div>
            <span className="com-rail-group-status com-rail-group-status--joined">Joined</span>
          </div>
          <div className="com-rail-group-row">
            <div className="com-rail-group-icon" aria-hidden />
            <div style={{ minWidth: 0 }}>
              <div className="com-rail-group-name">Low AMH Warriors</div>
              <div className="com-rail-group-sub">2,100 · Nova matched you</div>
            </div>
            <button type="button" className="com-rail-group-status com-rail-group-status--join" onClick={() => navigate?.('/signup')}>
              Join
            </button>
          </div>
          <div className="com-rail-group-row">
            <div className="com-rail-group-icon" aria-hidden />
            <div style={{ minWidth: 0 }}>
              <div className="com-rail-group-name">Fertility &amp; Mental Health</div>
              <div className="com-rail-group-sub">1,890 members</div>
            </div>
            <button type="button" className="com-rail-group-status com-rail-group-status--join" onClick={() => navigate?.('/signup')}>
              Join
            </button>
          </div>
          <div className="com-rail-group-row">
            <div className="com-rail-group-icon" aria-hidden />
            <div style={{ minWidth: 0 }}>
              <div className="com-rail-group-name">PCOS Sisters</div>
              <div className="com-rail-group-sub">4,100 · Nova matched you</div>
            </div>
            <button type="button" className="com-rail-group-status com-rail-group-status--join" onClick={() => navigate?.('/signup')}>
              Join
            </button>
          </div>

          <div className="com-rail-label">Ask Nova about community</div>
        </div>

        <div className="fec-ns-aside-divider" />

        <NovaSidebarGradientBar
          chatExpanded={novaOpen}
          onToggleChat={toggleChat}
          chapterKey={chapterKey}
          pillText={c.novaSubline}
          section={section}
          connectMode
        />

        {novaOpen ? (
          <div className="fec-ns-connect-chat">
            <div className="fec-ns-connect-chat__chips">
              {COMMUNITY_ASK_CHIPS.map((text) => (
                <button key={text} type="button" className="fec-ns-connect-chat__chip" onClick={() => handleChipClick(text)}>
                  {text}
                </button>
              ))}
            </div>
            <div className="fec-ns-connect-chat__row">
              <input
                type="text"
                className="fec-ns-connect-chat__input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Ask anything..."
              />
              <button type="button" className="fec-ns-connect-chat__send" onClick={handleSend} aria-label="Send to Nova">
                <svg viewBox="0 0 10 10" width="12" height="12" style={{ transform: 'rotate(-90deg)' }}>
                  <path
                    d="M2 5h6M5 2l3 3-3 3"
                    stroke="white"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (isConnectRail) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 0%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <div
          className="fec-connect-aside-rail"
          style={{
            flex: '1 1 0%',
            minHeight: 0,
            overflowY: 'auto',
            background: 'var(--white)',
          }}
        >
          <div className="fec-rail-page-eyebrow">AT A GLANCE</div>
          <div className="fec-rail-page-title">Your connection hub</div>

          <div className="fec-rail-label">Already connected</div>
          <div className="fec-rail-row">
            <div className="fec-rail-av fec-av-jasmine">
              JM
              <span className="fec-rail-av__dot" aria-hidden />
            </div>
            <div className="fec-rail-text">
              <div className="fec-rail-name">Jasmine M.</div>
              <div className="fec-rail-sub">Stim day 8 · Columbus</div>
            </div>
            <button type="button" className="fec-rail-msg" onClick={() => navigate?.('/signup')}>
              Message
            </button>
          </div>
          <div className="fec-rail-row">
            <div className="fec-rail-av fec-av-ava">
              AK
              <span className="fec-rail-av__dot" aria-hidden />
            </div>
            <div className="fec-rail-text">
              <div className="fec-rail-name">Ava K.</div>
              <div className="fec-rail-sub">Transfer prep · Cincinnati</div>
            </div>
            <button type="button" className="fec-rail-msg" onClick={() => navigate?.('/signup')}>
              Message
            </button>
          </div>

          <div className="fec-rail-label">Live rooms now</div>
          <div className="fec-ns-live-room">
            <span className="fec-ns-live-dot" aria-hidden />
            <div className="fec-ns-live-copy">
              <div className="fec-ns-live-name">Stims support</div>
              <div className="fec-ns-live-sub">7 women · stim day 8</div>
              <div className="fec-ns-live-footer">
                <span className="fec-ns-live-count">7 in room</span>
                <button type="button" className="fec-ns-live-join" onClick={() => navigate?.('/signup')}>
                  Join →
                </button>
              </div>
            </div>
          </div>
          <div className="fec-ns-live-room">
            <span className="fec-ns-live-dot" aria-hidden />
            <div className="fec-ns-live-copy">
              <div className="fec-ns-live-name">Columbus fertility chat</div>
              <div className="fec-ns-live-sub">6 women · near you</div>
              <div className="fec-ns-live-footer">
                <span className="fec-ns-live-count">6 in room</span>
                <button type="button" className="fec-ns-live-join" onClick={() => navigate?.('/signup')}>
                  Join →
                </button>
              </div>
            </div>
          </div>

          <div className="fec-rail-label">My groups</div>
          <div className="fec-ns-group-row">
            <div className="fec-ns-group-name">IVF Warriors — Cycle 1</div>
            <div className="fec-ns-group-sub">3,240 members · your cycle</div>
            <div className="fec-ns-group-status fec-ns-group-status--joined">Joined</div>
          </div>
          <div className="fec-ns-group-row">
            <div className="fec-ns-group-name">Low AMH Warriors</div>
            <div className="fec-ns-group-sub">2,100 members</div>
            <button type="button" className="fec-ns-group-status fec-ns-group-status--join">
              Join →
            </button>
          </div>
          <div className="fec-ns-group-row">
            <div className="fec-ns-group-name">PCOS Sisters</div>
            <div className="fec-ns-group-sub">4,100 members</div>
            <button type="button" className="fec-ns-group-status fec-ns-group-status--join">
              Join →
            </button>
          </div>

          <div className="fec-rail-label">Registered</div>
          <div className="fec-ns-coming-row">
            <span className="fec-ns-time-badge">Tonight 8pm</span>
            <div>
              <div className="fec-ns-coming-name">Night before retrieval</div>
              <div className="fec-ns-coming-sub">14 registered · nurse led</div>
            </div>
          </div>
          <div className="fec-ns-coming-row">
            <span className="fec-ns-time-badge">Fri 12pm</span>
            <div>
              <div className="fec-ns-coming-name">Low AMH Q&A</div>
              <div className="fec-ns-coming-sub">8 spots left · RE hosted</div>
            </div>
          </div>
        </div>

        <div className="fec-ns-aside-divider" />

        <NovaSidebarGradientBar
          chatExpanded={novaOpen}
          onToggleChat={toggleChat}
          chapterKey={chapterKey}
          pillText="Watching over your journey"
          section={section}
          connectMode
        />

        {novaOpen ? (
          <div className="fec-ns-connect-chat">
            <div className="fec-ns-connect-chat__chips">
              {CONNECT_ASK_CHIPS.map((text) => (
                <button key={text} type="button" className="fec-ns-connect-chat__chip" onClick={() => handleChipClick(text)}>
                  {text}
                </button>
              ))}
            </div>
            <div className="fec-ns-connect-chat__row">
              <input
                type="text"
                className="fec-ns-connect-chat__input"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Message Nova..."
              />
              <button type="button" className="fec-ns-connect-chat__send" onClick={handleSend} aria-label="Send to Nova">
                <svg viewBox="0 0 10 10" width="12" height="12" style={{ transform: 'rotate(-90deg)' }}>
                  <path
                    d="M2 5h6M5 2l3 3-3 3"
                    stroke="white"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 0%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '12px 18px 10px',
          borderBottom: '0.5px solid rgba(212, 83, 126, 0.1)',
          flexShrink: 0,
          background: 'var(--white)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'rgb(212, 83, 126)',
                marginBottom: 2,
              }}
            >
              Nova&apos;s Daily
            </div>
            <div className="nova-feed-rail-page-title">Recommendations</div>
          </div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: 'rgb(153, 53, 86)',
              background: 'rgba(212, 83, 126, 0.08)',
              border: '0.5px solid rgba(212, 83, 126, 0.2)',
              borderRadius: 999,
              padding: '3px 9px',
            }}
          >
            Stim day 8
          </div>
        </div>
      </div>

      <div
        style={{
          flex: '1 1 0%',
          minHeight: 0,
          overflowY: 'auto',
          padding: '12px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          background: 'var(--white)',
        }}
      >
        {isFertilityDoctors ? (
          <div
            style={{
              background: '#fbeaf0',
              borderRadius: 10,
              padding: '10px 14px',
              border: '0.5px solid rgba(212, 83, 126, 0.15)',
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#993556',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: 3,
              }}
            >
              Nova recommends · stim day 8 · IVF support
            </div>
            <div style={{ fontSize: 11, color: '#4b1528', marginBottom: 10, lineHeight: 1.45 }}>
              Acupuncture alongside IVF can support follicle development and reduce retrieval anxiety. These practitioners work with fertility patients in Columbus.
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '0.5px solid var(--sand, #ede8df)' }}>
              <DoctorPhoto36
                src={EMMA_IMG}
                alt="Emma Cheung, L.Ac"
                initials="EC"
                fallbackBg="#eeedfe"
                fallbackColor="#534ab7"
                border="2px solid #eeedfe"
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Emma Cheung, L.Ac</span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 10,
                      background: 'rgba(212,83,126,0.15)',
                      color: '#993556',
                      marginLeft: 2,
                    }}
                  >
                    IVF support
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>Acupuncture · Westerville · 4 miles</div>
                <div style={{ fontSize: 10, color: 'var(--muted)' }}>★★★★★ 5.0 · 18 OrivonAI reviews</div>
              </div>
              <button
                type="button"
                style={{
                  background: '#e1f5ee',
                  color: '#085041',
                  border: '0.5px solid #1d9e75',
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '5px 10px',
                  borderRadius: 8,
                  cursor: 'default',
                  fontFamily: 'inherit',
                  flexShrink: 0,
                }}
              >
                ✓ Saved
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0' }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: '#eeedfe',
                  color: '#534ab7',
                  fontSize: 11,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                BL
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Brette Luck, L.Ac</span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '2px 8px',
                      borderRadius: 10,
                      background: 'rgba(212,83,126,0.15)',
                      color: '#993556',
                      marginLeft: 2,
                    }}
                  >
                    Fertility specialist
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>Ohio Acupuncture & Chinese Herbal Medicine · Columbus · 7 miles</div>
                <div style={{ fontSize: 10, color: 'var(--muted)' }}>★★★★★ 4.9 · 12 OrivonAI reviews</div>
              </div>
              <button
                type="button"
                onClick={() => navigate?.('/signup')}
                style={{
                  border: '0.5px solid #f4c0d1',
                  background: 'var(--white)',
                  color: '#993556',
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '5px 10px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  flexShrink: 0,
                }}
              >
                + Save
              </button>
            </div>
          </div>
        ) : isLgbtqFertility ? (
          <>
            <div style={contentCard}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--pink-d)',
                  marginBottom: 4,
                }}
              >
                INCLUSIVE SUPPORT
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22', marginBottom: 4 }}>{inc.title}</div>
              <div style={{ fontSize: 12, color: TEXT_TERTIARY, lineHeight: 1.5 }}>{inc.body}</div>
              <button type="button" onClick={() => handleChipClick(inc.link.replace(/\s*→$/, ''))} style={linkBtn}>
                {inc.link}
              </button>
            </div>

            <div style={contentCard}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--pink-d)',
                  marginBottom: 4,
                }}
              >
                LEGAL
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22', marginBottom: 4 }}>Parental rights in Ohio</div>
              <div style={{ fontSize: 12, color: TEXT_TERTIARY, lineHeight: 1.5 }}>
                Second-parent adoption, pre-birth orders, and donor agreements. Nova has prepared a legal checklist for your path.
              </div>
              <button type="button" onClick={() => handleChipClick('Legal checklist for my path')} style={linkBtn}>
                See legal guide →
              </button>
            </div>

            <div style={contentCard}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--pink-d)',
                  marginBottom: 4,
                }}
              >
                ARTICLE
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22', marginBottom: 4 }}>{articleTitle}</div>
              <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginTop: 4 }}>Clinically reviewed · 5 min read</div>
              <button type="button" onClick={() => navigate?.(demoBase ? `${demoBase}/resources` : '/resources')} style={linkBtn}>
                Read →
              </button>
            </div>
          </>
        ) : isTtcCycle ? (
          <>
            <div
              style={{
                background: 'var(--purple-l)',
                border: '0.5px solid var(--purple-m)',
                borderRadius: 10,
                padding: '12px 16px',
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--purple-d)', marginBottom: 4 }}>
                Timing window
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22', marginBottom: 4 }}>Your best 24–48 hours</div>
              <div style={{ fontSize: 12, color: '#2d1a22', lineHeight: 1.6 }}>
                OPK peak + BBT shift together today. This is your strongest signal across 4 months. Peak timing is today and tomorrow.
              </div>
              <button type="button" onClick={() => handleChipClick('Timing checklist for peak days')} style={linkBtn}>
                Nova prepared a timing checklist →
              </button>
            </div>

            <div style={contentCard}>
              <span
                style={{
                  fontSize: 10,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: '#E1F5EE',
                  color: '#0F6E56',
                  fontWeight: 500,
                }}
              >
                Tip
              </span>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22', marginTop: 6 }}>Hydration and cervical fluid</div>
              <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginTop: 4, lineHeight: 1.4 }}>
                Mild dehydration makes CM harder to read. Small, steady water intake beats chugging right before you log.
              </div>
              <button type="button" onClick={() => handleChipClick('How hydration affects cervical mucus')} style={linkBtn}>
                Nova explains more →
              </button>
            </div>

            <div style={contentCard}>
              <span
                style={{
                  fontSize: 10,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: '#EEEDFE',
                  color: '#534AB7',
                  fontWeight: 500,
                }}
              >
                Article
              </span>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22', marginTop: 6 }}>What a short luteal phase means</div>
              <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginTop: 4 }}>Clinically reviewed · 4 min read</div>
              <button
                type="button"
                onClick={() => navigate?.(demoBase ? `${demoBase}/resources` : '/resources')}
                style={linkBtn}
              >
                Read →
              </button>
            </div>
          </>
        ) : (
          <>
            {/* A) ORA NOTIFICATION */}
            <div
              style={{
                background: '#FBEAF0',
                border: '0.5px solid rgba(212,83,126,0.15)',
                borderRadius: 10,
                padding: '12px 16px',
              }}
            >
              <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', color: '#D4537E', marginBottom: 4 }}>
                {c.headsUpTitle}
              </div>
              <div style={{ fontSize: 12, color: '#2d1a22', lineHeight: 1.6 }}>{c.headsUpBody}</div>
              <div style={{ fontSize: 11, color: 'rgba(60,30,45,0.4)', marginTop: 4 }}>Today · 7:02am</div>
            </div>

            {/* B) TIP CARD */}
            <div style={contentCard}>
              <span
                style={{
                  fontSize: 10,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: '#E1F5EE',
                  color: '#0F6E56',
                  fontWeight: 500,
                }}
              >
                Tip
              </span>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22', marginTop: 6 }}>{c.tipTitle}</div>
              <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginTop: 4, lineHeight: 1.4 }}>{c.tipBody}</div>
              <button
                type="button"
                onClick={() => handleChipClick(c.tipChip)}
                style={linkBtn}
              >
                Nova explains more →
              </button>
            </div>

            {/* C) ARTICLE CARD */}
            <div style={contentCard}>
              <span
                style={{
                  fontSize: 10,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: '#EEEDFE',
                  color: '#534AB7',
                  fontWeight: 500,
                }}
              >
                Article
              </span>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#2d1a22', marginTop: 6 }}>{c.articleTitle}</div>
              <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginTop: 4 }}>{c.articleMeta}</div>
              <button
                type="button"
                onClick={() => navigate?.(demoBase ? `${demoBase}/resources` : '/resources')}
                style={linkBtn}
              >
                Read →
              </button>
            </div>

            {/* Today's reminder */}
            <div>
              <div style={sectionLabel}>{c.reminderTitle}</div>
              <div
                style={{
                  borderLeft: '2.5px solid rgba(212,83,126,0.3)',
                  borderRadius: '0 8px 8px 0',
                  background: BG_SECONDARY,
                  padding: '10px 12px',
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontStyle: 'italic',
                    color: '#2d1a22',
                    lineHeight: 1.65,
                  }}
                >
                  {c.quoteBody}
                </div>
                <div style={{ fontSize: 11, color: TEXT_TERTIARY, marginTop: 4 }}>{c.quoteAttribution}</div>
              </div>
            </div>
          </>
        )}
      </div>

      <NovaSidebarGradientBar
        chatExpanded={novaOpen}
        onToggleChat={toggleChat}
        chapterKey={chapterKey}
        pillText={c.novaSubline}
        section={section}
      />

      {novaOpen ? (
        <div
          style={{
            flexShrink: 0,
            background: 'var(--white)',
            borderTop: '0.5px solid rgba(212, 83, 126, 0.08)',
            padding: '10px 14px 14px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            {(c.askChips || []).map((text) => (
              <button
                key={text}
                type="button"
                onClick={() => handleChipClick(text)}
                style={{
                  background: '#FBEAF0',
                  color: '#111111',
                  border: '0.5px solid rgba(212,83,126,0.2)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  fontSize: 11,
                  fontWeight: 400,
                  cursor: 'pointer',
                  lineHeight: 1.45,
                  textAlign: 'left',
                  fontFamily: 'inherit',
                }}
              >
                {text}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              placeholder="Message Nova…"
              style={{
                flex: 1,
                fontSize: 13,
                background: BG_SECONDARY,
                border: `0.5px solid ${inputFocused ? '#D4537E' : BORDER_SECONDARY}`,
                borderRadius: 10,
                padding: '10px 14px',
                outline: 'none',
                fontFamily: 'inherit',
              }}
            />
            <button
              type="button"
              onClick={handleSend}
              aria-label="Send to Nova"
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: '#D4537E',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <svg viewBox="0 0 10 10" width="12" height="12" style={{ transform: 'rotate(-90deg)' }}>
                <path
                  d="M2 5h6M5 2l3 3-3 3"
                  stroke="white"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
